import { OnchainWallet, OnchainAsset } from './onchainTypes';
import { readEvmWalletAssets } from './evmReader';
import { readSolanaWalletAssets } from './solanaReader';

// --- Cache mémoire pour TAO ---
const TAO_CACHE_TTL = 300_000; // 5 min
type TaoCacheEntry = {
  assets: OnchainAsset[];
  timestamp: number;
};
const taoCache: Map<string, TaoCacheEntry> = new Map();
const inFlightRequests: Map<string, Promise<OnchainAsset[]>> = new Map();

export class OnchainService {
  static async fetchAssets(wallet: OnchainWallet): Promise<OnchainAsset[]> {
    if (wallet.chain === 'ethereum') {
      return await readEvmWalletAssets(wallet.address, wallet.id);
    }
    if (wallet.chain === 'solana') {
      return await readSolanaWalletAssets(wallet.address, wallet.id);
    }
    if (wallet.chain === 'tao') {
      const cacheKey = wallet.address + ':tao';
      const now = Date.now();
      // Vérifie le cache
      const cached = taoCache.get(cacheKey);
      if (cached && now - cached.timestamp < TAO_CACHE_TTL) {
        // Refresh async si proche de l'expiration (optionnel)
        if (now - cached.timestamp > TAO_CACHE_TTL * 0.8) {
          OnchainService.refreshTao(wallet, cacheKey);
        }
        return cached.assets;
      }
      // Déduplication des requêtes
      if (inFlightRequests.has(cacheKey)) {
        return inFlightRequests.get(cacheKey)!;
      }
      const req = (async () => {
        try {
          const res = await fetch(`/api/onchain/tao-balance?address=${wallet.address}`);
          if (!res.ok) {
            throw new Error(`TAO balance request failed with status ${res.status}`);
          }
          const data = await res.json();
          let assets: OnchainAsset[] = [];
          if (data && typeof data.balance === 'number') {
            assets = [{ symbol: 'TAO', quantity: data.balance, chain: 'tao', walletId: wallet.id }];
            taoCache.set(cacheKey, { assets, timestamp: Date.now() });
          }
          return assets;
        } catch (e) {
          return [];
        } finally {
          inFlightRequests.delete(cacheKey);
        }
      })();
      inFlightRequests.set(cacheKey, req);
      return req;
    }
    return [];
  }

  // Refresh async du cache TAO (background)
  static async refreshTao(wallet: OnchainWallet, cacheKey: string) {
    try {
      const res = await fetch(`/api/onchain/tao-balance?address=${wallet.address}`);
      if (!res.ok) {
        throw new Error(`TAO balance request failed with status ${res.status}`);
      }
      const data = await res.json();
      let assets: OnchainAsset[] = [];
      if (data && typeof data.balance === 'number') {
        assets = [{ symbol: 'TAO', quantity: data.balance, chain: 'tao', walletId: wallet.id }];
        taoCache.set(cacheKey, { assets, timestamp: Date.now() });
      }
    } catch (e) { /* ignore */ }
  }
}
